/**
 * HCP Search Form Component
 * Filter form for HCP search with validation
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { ENV } from '../../../../config/environment';

const BASE_SOURCES = ['ONEKEY', 'VEEVA', 'XPT', 'SP'];

const getSelectProps = (placeholder) => ({
  displayEmpty: true,
  renderValue: (selected) => selected || placeholder,
});

const getInputPlaceholder = (label) => `Enter ${label}`;

const HCPSearchForm = ({
  filters,
  facets,
  mode,
  onModeChange,
  onUpdateFilters,
  onUpdateFiltersAndSearch,
  onSearch,
  onClear,
  autoSearch,
  onAutoSearchChange,
  loading,
}) => {
  const theme = useTheme();
  const [openTips, setOpenTips] = React.useState(false);
  const sourceOptions = React.useMemo(() => {
    const clientName = String(ENV.BROWSER_CLIENT_NAME || '').trim().toLowerCase();
    const clientSources = [];

    if (clientName === 'acemdm') {
      clientSources.push('LAAD');
    }

    if (clientName === 'leo') {
      clientSources.push('ENOVA');
    }

    const backendSources = Array.isArray(facets?.source) ? facets.source : [];
    const merged = [...BASE_SOURCES, ...clientSources, ...backendSources]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);

    return [...new Set(merged)];
  }, [facets?.source]);

  const handleChange = (field) => (event) => {
    onUpdateFilters({ [field]: event.target.value });
  };

  const handleSourceToggle = (value) => {
    const current = Array.isArray(filters.source) ? filters.source : [];
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    onUpdateFiltersAndSearch({ source: next });
  };

  const handleClearField = (field) => () => {
    onUpdateFiltersAndSearch({ [field]: '' });
  };

  const handleClearSource = (value) => () => {
    const current = Array.isArray(filters.source) ? filters.source : [];
    onUpdateFiltersAndSearch({ source: current.filter((item) => item !== value) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch();
  };

  const softInputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: '4px',
      height: '36px',
      transition: 'all 0.15s ease',
      '& fieldset': {
        borderColor: '#e2e8f0',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#cbd5e1',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2196f3',
        borderWidth: '1px',
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: '0 2px 6px rgba(33, 150, 243, 0.06)',
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
      
      color: '#0f172a',
      height: '32px',
      boxSizing: 'border-box',
      '&::placeholder': {
        
      }
    },
    '& .MuiInputLabel-root': {
      
      color: '#64748b',
    },
    '& .MuiSelect-select': {
      padding: '0 10px !important',
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      
      color: '#0f172a',
      boxSizing: 'border-box',
    }
  };

  const FieldLabel = ({ children }) => (
    <Typography sx={{ fontWeight: 600, color: '#475569', mb: 0.4, ml: 0.5,  lineHeight: 1.2, display: 'block' }}>
      {children}
    </Typography>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Tabs
          value={mode}
          onChange={(e, v) => onModeChange(v)}
          TabIndicatorProps={{
            style: {
              backgroundColor: theme.palette.primary.main,
              height: '3px',
              borderTopLeftRadius: '3px',
              borderTopRightRadius: '3px',
            }
          }}
          sx={{
            minHeight: '44px',
            '& .MuiTabs-flexContainer': { gap: 4 }
          }}
        >
          <Tab
            label="Basic Search"
            value="basic"
            disableRipple
            sx={{
              minHeight: '44px',
              py: 1,
              px: 0.5,
              textTransform: 'none',
              fontWeight: mode === 'basic' ? 600 : 500,
              color: mode === 'basic' ? theme.palette.primary.main : '#64748b',
              transition: 'color 0.2s',
              opacity: 1,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
              },
              '&:hover': {
                color: mode === 'basic' ? theme.palette.primary.main : '#334155'
              }
            }}
          />
          <Tab
            label="Advanced Search"
            value="advanced"
            disableRipple
            sx={{
              minHeight: '44px',
              py: 1,
              px: 0.5,
              textTransform: 'none',
              fontWeight: mode === 'advanced' ? 600 : 500,
              
              color: mode === 'advanced' ? theme.palette.primary.main : '#64748b',
              transition: 'color 0.2s',
              opacity: 1,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
              },
              '&:hover': {
                color: mode === 'advanced' ? theme.palette.primary.main : '#334155'
              }
            }}
          />
        </Tabs>

        {mode === 'advanced' && (
          <Button className="action-btn process-btn"
            variant="text"
            size="small"
            onClick={() => setOpenTips(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: theme.palette.primary.main,
              
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
            }}
          >
            Search Tips
          </Button>
        )}
      </Box>

      {/* Main Search Bar */}
      <Box sx={{ mb: 1.8 }}>
        <FieldLabel>Quick Search</FieldLabel>
        <TextField
          sx={{
            ...softInputSx,
            '& .MuiOutlinedInput-root': {
              ...softInputSx['& .MuiOutlinedInput-root'],
              backgroundColor: '#fff',
              borderRadius: '10px',
            },
            '& .MuiOutlinedInput-input': { padding: '12px 18px',  color: '#0f172a', '&::placeholder': {  } },
          }}
          fullWidth
          placeholder={mode === 'basic' ? 'Search by name, ID, or keyword...' : 'Enter professional details, specialty, or location keywords...'}
          value={mode === 'basic' ? filters.basicText : filters.advancedText}
          onChange={handleChange(mode === 'basic' ? 'basicText' : 'advancedText')}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                <SearchIcon sx={{ color: theme.palette.primary.main,  }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filter Grid */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FieldLabel>Profession</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth select size="small"
            value={filters.professions}
            onChange={handleChange('professions')}
            disabled={loading}
            {...getSelectProps('Select Profession')}
          >
            <MenuItem value="" sx={{  }}>All Professions</MenuItem>
            {(facets?.professions || []).map((item) => (
              <MenuItem key={item} value={item} sx={{  }}>{item}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FieldLabel>Specialty</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth select size="small"
            value={filters.specialities}
            onChange={handleChange('specialities')}
            disabled={loading}
            {...getSelectProps('Select Specialty')}
          >
            <MenuItem value="">All Specialties</MenuItem>
            {(facets?.specialities || []).map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FieldLabel>Location (State)</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth select size="small"
            value={filters.state}
            onChange={handleChange('state')}
            disabled={loading}
            {...getSelectProps('Select State')}
          >
            <MenuItem value="">All States</MenuItem>
            {(facets?.state || []).map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FieldLabel>Zip Code</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth size="small"
            placeholder="e.g. 90210"
            value={filters.zip5}
            onChange={handleChange('zip5')}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FieldLabel>NPI ID</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth size="small"
            placeholder="10-digit NPI"
            value={filters.npiId}
            onChange={handleChange('npiId')}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FieldLabel>Account Status</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth select size="small"
            value={filters.status}
            onChange={handleChange('status')}
            disabled={loading}
            {...getSelectProps('Select Status')}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {(facets?.status || []).map((item) => (
              <MenuItem key={item} value={item}>{item}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FieldLabel>Gender</FieldLabel>
          <TextField
            sx={softInputSx}
            fullWidth
            select
            size="small"
            value={filters.gender}
            onChange={handleChange('gender')}
            disabled={loading}
            {...getSelectProps('Select Gender')}
          >
            <MenuItem value="">All Genders</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button className="action-btn process-btn"
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            disableElevation
            sx={{
              height: 38,
              borderRadius: '8px',
              fontWeight: 600,
              textTransform: 'none',
              
              backgroundColor: theme.palette.primary.main,
              boxShadow: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transform: 'translateY(-1px)'
              },
              '&.Mui-disabled': {
                backgroundColor: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
                transform: 'none'
              }
            }}
            startIcon={<SearchIcon />}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </Grid>
      </Grid>

      {/* Sources and Active Filters */}
      {(() => {
        const hasActiveFilters = Boolean(
          filters.professions ||
          filters.specialities ||
          filters.state ||
          filters.status ||
          filters.gender ||
          filters.zip5 ||
          filters.npiId ||
          (filters.source && filters.source.length > 0)
        );

        return (
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FieldLabel>
                Data Sources
              </FieldLabel>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {sourceOptions.map((src) => {
                  const isChecked = (filters.source || []).includes(src);
                  return (
                    <Chip
                      key={src}
                      label={src}
                      onClick={() => handleSourceToggle(src)}
                      variant={isChecked ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        
                        height: '28px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        backgroundColor: isChecked ? `${theme.palette.primary.main} !important` : 'transparent',
                        color: isChecked ? '#ffffff !important' : '#475569',
                        borderColor: isChecked ? theme.palette.primary.main : '#cbd5e1',
                        '&:hover': {
                          backgroundColor: isChecked ? theme.palette.primary.dark : '#f1f5f9',
                          borderColor: isChecked ? theme.palette.primary.dark : '#94a3b8',
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {hasActiveFilters && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <FieldLabel>
                  Active Selection
                </FieldLabel>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                  {filters.npiId && <Chip deleteIcon={<CloseIcon
                    sx={{
                      fontSize: "15px !important",
                      color: "#64748b",
                      transition: "0.2s",
                      mr: 1,
                      cursor: "pointer",

                      "&:hover": {
                        color: "#ef4444",
                      },
                    }}
                  />} label={`NPI: ${filters.npiId}`} size="small" onDelete={handleClearField('npiId')} sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />}
                  {filters.professions && <Chip deleteIcon={<CloseIcon
                    sx={{
                      fontSize: "15px !important",
                      color: "#64748b",
                      transition: "0.2s",
                      mr: 1,
                      cursor: "pointer",

                      "&:hover": {
                        color: "#ef4444",
                      },
                    }}
                  />} label={filters.professions} size="small" onDelete={handleClearField('professions')} sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />}
                  {filters.specialities && <Chip deleteIcon={<CloseIcon
                    sx={{
                      fontSize: "15px !important",
                      color: "#64748b",
                      transition: "0.2s",
                      mr: 1,
                      cursor: "pointer",

                      "&:hover": {
                        color: "#ef4444",
                      },
                    }}
                  />} label={filters.specialities} size="small" onDelete={handleClearField('specialities')} sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />}
                  {filters.state && <Chip deleteIcon={<CloseIcon
                    sx={{
                      fontSize: "15px !important",
                      color: "#64748b",
                      transition: "0.2s",
                      mr: 1,
                      cursor: "pointer",

                      "&:hover": {
                        color: "#ef4444",
                      },
                    }}
                  />} label={filters.state} size="small" onDelete={handleClearField('state')} sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />}
                  {filters.status && <Chip deleteIcon={<CloseIcon
                    sx={{
                      fontSize: "15px !important",
                      color: "#64748b",
                      transition: "0.2s",
                      mr: 1,
                      cursor: "pointer",

                      "&:hover": {
                        color: "#ef4444",
                      },
                    }}
                  />} label={filters.status} size="small" onDelete={handleClearField('status')} sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />}
                  {filters.gender && <Chip deleteIcon={<CloseIcon
                    sx={{
                      fontSize: "15px !important",
                      color: "#64748b",
                      transition: "0.2s",
                      mr: 1,
                      cursor: "pointer",

                      "&:hover": {
                        color: "#ef4444",
                      },
                    }}
                  />} label={filters.gender} size="small" onDelete={handleClearField('gender')} sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />}
                  {filters.zip5 && <Chip deleteIcon={<CloseIcon
                    sx={{
                      fontSize: "15px !important",
                      color: "#64748b",
                      transition: "0.2s",
                      mr: 1,
                      cursor: "pointer",

                      "&:hover": {
                        color: "#ef4444",
                      },
                    }}
                  />} label={filters.zip5} size="small" onDelete={handleClearField('zip5')} sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />}
                  {Array.isArray(filters.source) && filters.source.map((src) => (
                    <Chip key={src} label={src} size="small" onDelete={handleClearSource(src)}
                      deleteIcon={<CloseIcon
                        sx={{
                          fontSize: "15px !important",
                          color: "#64748b",
                          transition: "0.2s",
                          mr: 1,
                          cursor: "pointer",

                          "&:hover": {
                            color: "#ef4444",
                          },
                        }}
                      />}
                      sx={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }} />
                  ))}

                  <Button className="action-btn cancel-btn"
                    variant="text"
                    size="small"
                    onClick={onClear}
                    disabled={loading}
                    sx={{ textTransform: 'none', fontWeight: 600, color: theme.palette.primary.main,  minWidth: 'auto', p: 0, ml: 1 }}
                  >
                    Reset All
                  </Button></Box>
              </Box>
            )}
          </Box>
        );
      })()}

      <Dialog open={openTips} onClose={() => setOpenTips(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700,  pb: 1, pr: 6 }}>
          Search Tips
          <IconButton
            aria-label="close search tips"
            onClick={() => setOpenTips(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Example Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#1E88E5' }}>
              Example Query
            </Typography>
            <Box sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              mb: 2,
            }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#111827' }}>
                fname:tim* AND lname:white~
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              <strong>Note:</strong> When specifying Boolean operators such as AND, OR, or NOT, the keywords must appear in uppercase.
              Use <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>:</code> after field names (for example: <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>lname:white</code>).
              Use quotes for exact match.
            </Typography>
          </Box>

          {/* Field Mappings Section */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#1E88E5' }}>
              Field Name Mapping
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>MDM Id</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>mdm_id:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>HCP/HCE Id</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>hcp_hce_id:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>Ok Individual Id</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>ok_indv_id:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>First Name</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>fname:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>Middle Name</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>mname:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>Last Name</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>lname:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>Birth Date</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>birth_dt:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>Business Name</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>bus_name:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>Address Line 1</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>addr_ln_1_txt:</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>City</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>city:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>Country</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>cntry:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>ISN Id</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>isn_id:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>NPI Id</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>npi_id:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>SLN Id</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>sln_id:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>SLN Board Name</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>st_brd_nm:</Typography>
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#666' }}>SLN Type Name</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#111827' }}>st_lic_typ_nm:</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

HCPSearchForm.propTypes = {
  filters: PropTypes.shape({
    basicText: PropTypes.string,
    advancedText: PropTypes.string,
    professions: PropTypes.string,
    specialities: PropTypes.string,
    gender: PropTypes.string,
    status: PropTypes.string,
    state: PropTypes.string,
    zip5: PropTypes.string,
    npiId: PropTypes.string,
    suffix: PropTypes.string,
    source: PropTypes.arrayOf(PropTypes.string),
    isGoldenUniverse: PropTypes.string,
  }).isRequired,
  facets: PropTypes.shape({
    professions: PropTypes.array,
    specialities: PropTypes.array,
    gender: PropTypes.array,
    status: PropTypes.array,
    state: PropTypes.array,
    suffix: PropTypes.array,
  }),
  mode: PropTypes.string.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onUpdateFilters: PropTypes.func.isRequired,
  onUpdateFiltersAndSearch: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  autoSearch: PropTypes.bool.isRequired,
  onAutoSearchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default HCPSearchForm;
